import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { notification, Select, Tooltip, Typography } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { Building } from 'models/Building'
import CustomCheckBox from 'pages/building/floor/components/CustomCheckBox'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  buildingModeState,
  selectedPoint,
  selectedPointDetail,
  selectedSubImageState
} from 'store/buildingStore'
import useSWR from 'swr'
import { FloorMapElement } from 'types/building'
import Utils from 'utils'
import Krpano from 'utils/krpano'
import { KrpanoEngine } from 'utils/KrpanoEngine'

import Button from 'components/atoms/Button'
import LazyThumbnail from 'components/atoms/LazyThumbnail'
import Modal from 'components/atoms/Modal'
import Spinner from 'components/atoms/Spinner'
import Title from 'components/atoms/Title'
import DropdownIconCustom from 'components/icons/DropdownIconCustom'

export default function ModalAddStairway() {
  const { id, floorId } = useParams()
  const { t } = useTranslation()
  const { getDirectoryBasename, getBuiildingType, navigate } = useDirectory()

  const [isAddingStairway, setIsAddingStairway] = useState(false)
  const [newStair, setNewStair] = useState<{
    id?: string
    h?: number
    v?: number
    nextFloorId?: number
    nextPointId?: string
    nextPointOrder?: number
  }>()

  const buildingMode = useRecoilValue(buildingModeState)
  const currentSelectedPoint = useRecoilValue(selectedPoint)
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const selectedSubImage = useRecoilValue(selectedSubImageState)

  const { data: building, isValidating: isValidatingFloor } = useSWR<Building>(
    {
      url: `/v1/buildings/by-type?id=${id}&building_type=${getBuiildingType()}`
    },
    {
      revalidateOnMount: false
    }
  )

  const { trigger: addEditStairway, isMutating } = useMutation(
    `v1/points/${currentSelectedPoint}/stairways`
  )

  const { data: mapItems, isValidating: isValidatingPoints } =
    useSWR<FloorMapElement>(
      newStair?.nextFloorId
        ? {
            url: 'v1/points',
            params: {
              buildingId: id,
              floorId: newStair?.nextFloorId
            }
          }
        : null
    )

  const listFloors = useMemo(
    () =>
      building?.floors
        .filter((item) => item.id !== Number(floorId))
        .map((floor) => ({
          value: floor.id,
          label: (
            <Tooltip
              placement="right"
              title={floor.name.length > 21 ? floor.name : undefined}
            >
              <span className="text-[#021120] text-sm">
                {Utils.truncateString(floor.name, 21)}
              </span>
            </Tooltip>
          )
        })) || [],
    [building?.floors, floorId]
  )

  const listPoints = useMemo(
    () =>
      mapItems?.points
        .filter((item) => item.image360?.thumbnail)
        .sort((a, b) => a.order - b.order) || [],
    [mapItems?.points]
  )

  const handleSelectFloor = (value: number) => {
    if (value !== newStair?.nextFloorId) {
      setNewStair((prev) => ({
        ...prev,
        nextFloorId: value,
        nextPointId: newStair?.id ? prev?.nextPointId : ''
      }))
    }
  }

  const handleCancel = () => {
    setNewStair(undefined)
    setIsAddingStairway(false)
  }

  // handle event of krpano
  const handleAddStairway = () => {
    ;(window as any).get_stair_position = () => {
      if (buildingMode.selectedMode === 'stairway') {
        const krpano = new KrpanoEngine('full').instance
        const h = krpano.get('mouseath')
        const v = krpano.get('mouseatv')
        setNewStair((prev) => ({
          ...prev,
          nextFloorId: listFloors.length ? listFloors[0].value : undefined,
          h: Number(h),
          v: Number(v)
        }))
        setIsAddingStairway(true)
      }
    }
  }

  // Legacy: Full Page Reload Strategy
  const handleStairwayFloorTransitionReload = useCallback(
    (targetFloor: string, targetPoint: string) => {
      localStorage.setItem('targetPoint', targetPoint)
      localStorage.setItem('previewMode', 'true')
      window.location.href = `${getDirectoryBasename()}/buildings/${id}/floors/${targetFloor}`
    },
    [getDirectoryBasename, id]
  )

  // New: In-Page SPA Transition Strategy
  const [, setBuildingMode] = useRecoilState(buildingModeState)
  const [, setCurrentSelectedPoint] = useRecoilState(selectedPoint)

  const handleStairwayFloorTransitionSpa = useCallback(
    (targetFloor: string, targetPoint: string) => {
      localStorage.setItem('targetPoint', targetPoint)
      localStorage.setItem('previewMode', 'true')
      setBuildingMode((prev) => ({
        ...prev,
        previewMode: true
      }))
      setCurrentSelectedPoint(targetPoint)
      navigate(
        `/buildings/${id}/floors/${targetFloor}?point=${targetPoint}&preview=true`
      )
    },
    [id, navigate, setBuildingMode, setCurrentSelectedPoint]
  )

  // handle click stairway
  const handleSelectStair = () => {
    ;(window as any).handle_click_stair = (
      stair_id: string,
      h: string,
      v: string,
      targetFloor: string,
      targetPoint: string
    ) => {
      if (buildingMode.selectedMode === 'direction') return

      if (buildingMode.selectedMode === 'stairway') {
        setNewStair({
          id: stair_id,
          h: Number(h),
          v: Number(v),
          nextFloorId: Number(targetFloor),
          nextPointId: targetPoint
        })
        setIsAddingStairway(true)
        return
      }

      if (!listFloors.find((item) => item.value === Number(targetFloor))) {
        !isMutating &&
          addEditStairway({
            method: 'delete',
            query: stair_id ? [stair_id] : undefined,
            data: null,
            params: {
              buildingId: id,
              floorId
            }
          })
        notification.error({
          key: 'stair_error',
          message: t('building.destinationNotFound')
        })
        setNewStair(undefined)
        const embedpano = new KrpanoEngine('full').instance
        embedpano.call(`removehotspot('stair-${stair_id}')`)
        return
      }

      const useSpaTransition = true
      if (useSpaTransition) {
        handleStairwayFloorTransitionSpa(targetFloor, targetPoint)
      } else {
        handleStairwayFloorTransitionReload(targetFloor, targetPoint)
      }
    }
  }

  const handleUpdateStairPosition = () => {
    ;(window as any).handle_update_stair_position = (
      stair_id: string,
      h: string,
      v: string,
      targetFloor: string,
      targetPoint: string
    ) => {
      const krpano = new KrpanoEngine('full').instance
      const inith = krpano.get('init_h')
      const initv = krpano.get('init_v')
      const droph = krpano.get('drop_h')
      const dropv = krpano.get('drop_v')

      const activeImageId = selectedSubImage || pointDetail?.image360?.id
      const delta = Krpano.GetSubImageDelta(pointDetail, activeImageId)
      const mainH = Krpano.NormalizeHGap(+h - delta)

      if (!isMutating && (droph !== inith || dropv !== initv)) {
        setPointDetail((prev) =>
          prev
            ? {
                ...prev,
                stairways: prev.stairways?.map((item) =>
                  item.id === stair_id
                    ? {
                        ...item,
                        h: mainH,
                        v: Number(v),
                        nextFloorId: targetFloor,
                        nextPointId: targetPoint
                      }
                    : item
                )
              }
            : prev
        )

        addEditStairway({
          method: 'patch',
          query: [stair_id],
          data: {
            buildingId: Number(id),
            floorId: Number(floorId),
            h: mainH,
            v: Number(v),
            nextFloorId: Number(targetFloor),
            nextPointId: targetPoint
          }
        })
      }
    }
  }

  const handleDelete = () => {
    !isMutating &&
      addEditStairway({
        method: 'delete',
        query: newStair?.id ? [newStair.id] : undefined,
        data: null,
        params: {
          buildingId: id,
          floorId
        },
        successMessage: t('organization.successDeletedNotification')
      })

    setNewStair(undefined)
    setPointDetail((prev) =>
      prev
        ? {
            ...prev,
            stairways: prev.stairways?.filter(
              (item) => item.id !== newStair?.id
            )
          }
        : prev
    )
    const embedpano = new KrpanoEngine('full').instance
    embedpano.call(`remove_stairway('${newStair?.id}')`)
    setIsAddingStairway(false)
  }

  const handleEnterSubmit = () => {
    const activeImageId = selectedSubImage || pointDetail?.image360?.id
    const delta = Krpano.GetSubImageDelta(pointDetail, activeImageId)
    const mainH = Krpano.NormalizeHGap(Number(newStair?.h) - delta)

    !isMutating &&
      addEditStairway(
        {
          method: newStair?.id ? 'patch' : 'post',
          query: newStair?.id ? [newStair.id] : undefined,
          data: {
            buildingId: Number(id),
            floorId: Number(floorId),
            h: mainH,
            v: Number(newStair?.v),
            nextFloorId: Number(newStair?.nextFloorId),
            nextPointId: newStair?.nextPointId
          },
          successMessage: t('organization.successNotification')
        },
        {
          onSuccess: (data) => {
            const embedpano = new KrpanoEngine('full').instance
            if (embedpano) {
              const floorName = building?.floors.find(
                (item) => item.id === Number(newStair?.nextFloorId)
              )?.name
              const newTooltip = Krpano.FormatStairName(floorName)
              if (newStair?.id) {
                embedpano.call(`remove_stairway('${newStair.id}')`)
              }
              const effectiveH = Krpano.NormalizeAngle((mainH || 0) + delta)
              const savedId = newStair?.id ? newStair.id : data.data.stairwayId
              embedpano.call(
                `add_stairway(${savedId}, ${effectiveH}, ${newStair?.v}, ${newStair?.nextFloorId}, ${newStair?.nextPointId}, ${newTooltip})`
              )
              setNewStair(undefined)
              setPointDetail((prev) => {
                if (!prev) return prev
                const newStairItem = {
                  id: String(savedId),
                  h: mainH,
                  v: Number(newStair?.v),
                  nextFloorId: String(newStair?.nextFloorId),
                  nextFloorName: floorName,
                  nextPointId: String(newStair?.nextPointId)
                }
                const exists = prev.stairways?.some(
                  (item) => item.id === newStairItem.id
                )
                return {
                  ...prev,
                  stairways: exists
                    ? prev.stairways?.map((item) =>
                        item.id === newStairItem.id ? newStairItem : item
                      )
                    : [...(prev.stairways || []), newStairItem]
                }
              })
            }
          }
        }
      )
    setIsAddingStairway(false)
  }

  handleAddStairway()
  handleSelectStair()
  handleUpdateStairPosition()

  if (!isAddingStairway) return null

  return (
    <Modal
      className="modal-custom"
      width={506}
      rootClassName=""
      isEnterable
      onEnter={handleEnterSubmit}
    >
      <Title title="building.settingStairway" />

      <div className="flex items-center gap-2 p-[10px] relative">
        <Typography className="font-bold">
          {t('common.sidebar.floor')}
        </Typography>
        <Select
          loading={isValidatingFloor}
          style={{
            width: 220,
            overflow: 'hidden'
          }}
          options={listFloors}
          value={newStair?.nextFloorId}
          onSelect={handleSelectFloor}
          disabled={!listFloors.length}
          className={`absolute left-1/2 -translate-x-1/2 ${
            !listFloors.length ? 'select-floot-disabled' : ''
          }`}
          suffixIcon={<DropdownIconCustom colorIcon="#021120" />}
        />
      </div>

      <div className="min-h-[360px] max-h-[360px] flex flex-col items-center gap-2 overflow-y-auto mt-2">
        {isValidatingPoints && <Spinner />}
        {!isValidatingPoints &&
          listPoints.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-2 cursor-pointer relative"
              onClick={() =>
                setNewStair((prev) => ({
                  ...prev,
                  nextPointId: item.id,
                  nextPointOrder: item.order
                }))
              }
            >
              <div className="absolute top-1/2 -translate-y-1/2 -left-10">
                <CustomCheckBox
                  id={item.id}
                  isDefault={false}
                  checked={newStair?.nextPointId}
                />
              </div>
              <div className="w-[220px] h-[110px] object-contain relative">
                <div className="absolute top-1 left-1 z-10 rounded-lg flex bg-[#021120] p-[2px] min-w-[16px]">
                  <div className="m-auto text-[13px] leading-[13px] font-bold text-white">
                    {item.order}
                  </div>
                </div>
                <LazyThumbnail
                  url={item.image360?.thumbnail || ''}
                  name={item.image360?.thumbnail || ''}
                  width={220}
                  height={110}
                />
              </div>
            </div>
          ))}
      </div>

      <div className="flex gap-10 justify-center mt-10">
        {newStair?.id ? (
          <Button
            sizing="w-[140px] h-[50px]"
            className="bg-[#B80808]"
            onClick={handleDelete}
          >
            {t('organization.buttonDelete')}
          </Button>
        ) : null}

        <Button sizing="w-[140px] h-[50px]" onClick={handleCancel}>
          {t('building.buttonCancel')}
        </Button>

        <Button
          onClick={handleEnterSubmit}
          type="primary"
          sizing="w-[140px] h-[50px]"
          disabled={!newStair?.nextPointId}
        >
          {t('building.buttonSave')}
        </Button>
      </div>
    </Modal>
  )
}
